import React, { useEffect, useRef, useState } from "react";

/**
 * Animated counter that starts when element enters viewport.
 * Supports number with optional prefix/suffix. If `value` string contains
 * non-numeric characters (like "50,000T" or "20+"), we animate the leading number.
 */
const CountUp = ({ value, duration = 1400, className = "" }) => {
  const [display, setDisplay] = useState(value);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const match = String(value).match(/^([^\d]*)([\d,.\s]+)(.*)$/);
    if (!match) {
      setDisplay(value);
      return;
    }
    const prefix = match[1];
    const numStr = match[2].replace(/[\s,]/g, "");
    const suffix = match[3];
    const target = parseFloat(numStr);
    if (isNaN(target)) {
      setDisplay(value);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              const current = target * eased;
              const formatted =
                target >= 1000
                  ? Math.round(current).toLocaleString("en-US")
                  : Number.isInteger(target)
                  ? Math.round(current).toString()
                  : current.toFixed(1);
              setDisplay(`${prefix}${formatted}${suffix}`);
              if (t < 1) requestAnimationFrame(tick);
              else setDisplay(value);
            };
            requestAnimationFrame(tick);
            io.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};

export default CountUp;
